<template>
  <section class="admin-content" id="assessment-detail-page">
    <Navbar />
    <main class="admin-main">
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center text-white flex-wrap" style="padding: 32px 0; gap: 12px;">
            <router-link to="/assessments" class="btn btn-sm btn-outline-light" aria-label="Back to assessments"><i class="fe fe-arrow-left"></i></router-link>
            <div class="header-icon-wrap"><i class="fe fe-clipboard" style="font-size: 22px;"></i></div>
            <div class="flex-fill">
              <h4 class="mb-0 font-weight-bold">{{ s ? s.form && s.form.title : "Assessment" }}</h4>
              <small style="opacity: .8;" v-if="s">
                {{ s.facility_name }} · {{ (s.assessment_date || s.created_at || "").slice(0, 10) }}
                <span v-if="s.submitted_by"> · by {{ s.submitted_by }}</span>
                · {{ s.source === "hefrias" ? "inspector (hefrias)" : "Claimify" }}
              </small>
            </div>
          </div>
        </div>
      </div>

      <section style="margin-top: -20px;">
        <div class="container pb-5">
          <div v-if="loading" class="card shadow-sm p-4 text-muted">Loading…</div>
          <div v-else-if="error" class="alert alert-danger">{{ error }}</div>

          <template v-else-if="s">
            <!-- Metrics -->
            <div class="row mb-2">
              <div class="col-6 col-md-3 mb-3">
                <div class="metric"><span class="metric-val" :class="scoreClass(s.metrics.percentile)">{{ s.metrics.percentile }}%</span><span class="metric-lbl">Score</span></div>
              </div>
              <div class="col-6 col-md-3 mb-3">
                <div class="metric"><span class="metric-val">{{ s.gap_totals.count }}</span><span class="metric-lbl">Gaps<span v-if="s.metrics.critical_gap_count"> · {{ s.metrics.critical_gap_count }} critical</span></span></div>
              </div>
              <div class="col-6 col-md-3 mb-3">
                <div class="metric"><span class="metric-val metric-val--sm">{{ money(s.gap_totals.low_cost) }} – {{ money(s.gap_totals.high_cost) }}</span><span class="metric-lbl">Standard price estimate</span></div>
              </div>
              <div class="col-6 col-md-3 mb-3">
                <div class="metric"><span class="metric-val metric-val--sm">{{ offers.totals.best_offers_total ? money(offers.totals.best_offers_total) : "—" }}</span>
                  <span class="metric-lbl">Best marketplace offers ({{ offers.totals.gaps_with_offers }}/{{ offers.totals.gaps }})</span></div>
              </div>
            </div>

            <!-- Gaps & offers -->
            <div class="card shadow-sm panel mb-4">
              <div class="panel-head">
                <div>
                  <h6 class="mb-0 font-weight-bold"><i class="fe fe-alert-triangle mr-1"></i>What is missing, and where to get it</h6>
                  <small class="text-muted">Quantities are the shortfall against the form's minimum. Prices are the vendors'; your agency's marketplace is listed first.</small>
                </div>
                <div v-if="isBuyer && offers.cart.length" class="d-flex flex-wrap" style="gap: 6px;">
                  <button type="button" class="btn btn-sm btn-submit" @click="addAll"><i class="fe fe-shopping-cart mr-1"></i>Add best offers to cart</button>
                  <router-link to="/marketplace/cart" class="btn btn-sm btn-light">Go to cart</router-link>
                </div>
              </div>
              <div v-if="!offers.gaps.length" class="p-4 text-center text-muted">
                <i class="fe fe-check-circle text-success" style="font-size: 2rem;"></i>
                <p class="mt-2 mb-0">No gaps found in this assessment.</p>
              </div>
              <div v-for="g in offers.gaps" :key="g.form_item_id" class="gap-row">
                <div class="gap-info">
                  <div class="font-weight-bold">{{ g.procurement_item ? g.procurement_item.name : g.question }}</div>
                  <div class="small text-muted">{{ g.category ? g.category + " · " : "" }}{{ g.question }}</div>
                  <div class="small mt-1">
                    <span class="gap-badge">{{ g.reason === "answered_no" ? "Answered " + (g.answer || "No") : `Has ${g.quantity || 0} of ${g.minimum_qty}` }}</span>
                    <strong class="ml-1">Need {{ g.shortfall_qty }}</strong>
                    <span v-if="g.low_cost !== null" class="text-muted"> · est. {{ money(g.low_cost) }} – {{ money(g.high_cost) }}</span>
                  </div>
                  <div v-if="g.comment" class="small text-muted mt-1"><i class="fe fe-message-square"></i> {{ g.comment }}</div>
                </div>
                <div class="gap-offers">
                  <div v-if="!g.procurement_item" class="small text-muted">No standard item is attached to this question, so it has no price or offers.</div>
                  <div v-else-if="!g.offers.length" class="small text-muted">No vendor offers this yet.</div>
                  <div v-for="(o, i) in g.offers" :key="o.vendor_product_id" class="offer" :class="{ 'offer--best': i === 0 }">
                    <div class="flex-fill">
                      <router-link :to="`/marketplace/products/${o.vendor_product_id}`" class="font-weight-bold">{{ o.name }}</router-link>
                      <div class="small text-muted">
                        {{ o.vendor ? o.vendor.name : "" }}
                        <span v-if="o.agency_marketplace" class="mk-badge">Your agency's marketplace</span>
                        <span v-if="!o.enough_stock" class="text-danger"> · only {{ o.quantity_available }} in stock</span>
                      </div>
                    </div>
                    <div class="text-right text-nowrap">
                      <div class="small">{{ money(o.unit_price) }} × {{ o.quantity }}</div>
                      <div class="font-weight-bold">{{ money(o.line_total) }}</div>
                    </div>
                    <button v-if="isBuyer" type="button" class="btn btn-sm btn-view ml-2" :aria-label="'Add ' + o.name + ' to cart'" @click="add(o)">
                      <i class="fe fe-plus"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Answers -->
            <div class="card shadow-sm panel">
              <div class="panel-head"><h6 class="mb-0 font-weight-bold"><i class="fe fe-list mr-1"></i>Answers</h6></div>
              <div v-for="group in answerGroups" :key="group.name" class="px-4 pb-3">
                <div class="ans-cat">{{ group.name }}</div>
                <div v-for="a in group.answers" :key="a.form_item_id" class="ans-row">
                  <div class="flex-fill">{{ a.question }}</div>
                  <div class="ans-val">
                    <span v-if="a.options.length">{{ a.options.map(o => o.name).join(", ") }}</span>
                    <span v-if="a.quantity !== null">{{ a.options.length ? " · " : "" }}{{ a.quantity }}</span>
                    <span v-if="a.response">{{ a.options.length || a.quantity !== null ? " · " : "" }}{{ a.response }}</span>
                    <span v-if="!a.options.length && a.quantity === null && !a.response" class="text-muted">—</span>
                    <div v-if="a.comment" class="small text-muted">{{ a.comment }}</div>
                    <div v-if="a.files.length" class="small">
                      <a v-for="(f, i) in a.files" :key="f.id" :href="f.url" target="_blank" rel="noopener" class="mr-2"><i class="fe fe-paperclip"></i> File {{ i + 1 }}</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </section>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import { API, money, errorText, scoreClass, stored, addOfferToCart } from "./assessments";

export default {
  components: { Navbar },
  data() {
    const user = stored("user") || {};
    return {
      s: null,
      offers: { gaps: [], cart: [], totals: {} },
      loading: true,
      error: "",
      isBuyer: user.type === "provider" || user.type === "provider_employee",
    };
  },
  computed: {
    answerGroups() {
      const groups = [];
      ((this.s && this.s.answers) || []).forEach((a) => {
        const name = a.category || "Other";
        let g = groups.find((x) => x.name === name);
        if (!g) groups.push((g = { name, answers: [] }));
        g.answers.push(a);
      });
      return groups;
    },
  },
  watch: {
    "$route.params.id"() {
      this.load();
    },
  },
  created() {
    this.load();
  },
  methods: {
    money,
    scoreClass,
    load() {
      this.loading = true;
      this.error = "";
      this.axios
        .get(`${API}/submissions/${this.$route.params.id}`)
        .then((res) => {
          this.s = res.data.data;
          this.offers = res.data.offers;
        })
        .catch((err) => (this.error = errorText(err, "Could not load the assessment.")))
        .finally(() => (this.loading = false));
    },
    add(offer) {
      addOfferToCart(offer);
      this.$toasted.success(`${offer.name} added to your cart`, { position: "top-center", duration: 3000 });
    },
    addAll() {
      this.offers.gaps.forEach((g) => g.offers.length && addOfferToCart(g.offers[0]));
      this.$toasted.success(`${this.offers.cart.length} item(s) added to your cart`, { position: "top-center", duration: 3000 });
    },
  },
};
</script>

<style scoped>
.page-header { background: linear-gradient(135deg, #115e59 0%, #0d9488 100%); }
.header-icon-wrap { width: 48px; height: 48px; background: rgba(255,255,255,0.15); border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.metric { background: #fff; border-radius: 12px; padding: 14px 16px; box-shadow: 0 2px 8px rgba(0,0,0,.06); height: 100%; }
.metric-val { display: block; font-size: 1.6rem; font-weight: 700; color: #134e4a; }
.metric-val--sm { font-size: 1.05rem; padding: 6px 0; }
.metric-lbl { font-size: .72rem; color: #64748b; text-transform: uppercase; letter-spacing: .04em; }
.score--good { color: #047857; }
.score--fair { color: #b45309; }
.score--poor { color: #b91c1c; }
.panel { border: none; border-radius: 12px; }
.panel-head { display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 10px; padding: 18px 20px 12px; }
.gap-row { display: flex; flex-wrap: wrap; gap: 16px; padding: 14px 20px; border-top: 1px solid #f1f5f9; }
.gap-info { flex: 1 1 260px; }
.gap-offers { flex: 2 1 360px; }
.gap-badge { background: #fee2e2; color: #991b1b; border-radius: 10px; padding: 1px 8px; font-size: .72rem; font-weight: 600; }
.offer { display: flex; align-items: center; gap: 10px; border: 1px solid #e2e8f0; border-radius: 10px; padding: 8px 12px; margin-bottom: 6px; }
.offer--best { border-color: #0d9488; background: #f0fdfa; }
.mk-badge { background: #ccfbf1; color: #115e59; border-radius: 10px; padding: 0 6px; font-size: .68rem; font-weight: 600; margin-left: 4px; }
.ans-cat { font-weight: 700; color: #134e4a; font-size: .85rem; text-transform: uppercase; letter-spacing: .04em; margin: 12px 0 4px; }
.ans-row { display: flex; gap: 16px; padding: 8px 0; border-bottom: 1px solid #f1f5f9; font-size: .88rem; }
.ans-val { width: 40%; text-align: right; font-weight: 600; }
.btn-submit { background: linear-gradient(135deg, #115e59, #0d9488); border: none; color: #fff; border-radius: 8px; font-weight: 600; }
.btn-submit:hover { opacity: .9; color: #fff; }
.btn-view { border-radius: 6px; border: 1px solid #e2e8f0; color: #115e59; background: #fff; font-size: .8rem; }
@media (max-width: 576px) { .ans-row { flex-direction: column; gap: 2px; } .ans-val { width: auto; text-align: left; } }
</style>
